// FieldDesk — карта с фильтрами, маркерами выездов и инженеров.
//
// Ключевые механики:
//   - Фильтры (период / инженеры / типы) собираются в шапке; при изменении —
//     debounce 300мс и перезагрузка GET /api/visits.
//   - Visits группируются по clientId → один маркер на координатах клиента,
//     с бейджем-числом и доминирующим цветом по type.
//   - Hover на маркер — компактный tooltip (Leaflet popup): клиент, адрес,
//     N выездов, превью первых 3.
//   - Click — drawer справа со ВСЕМИ визитами этого клиента в текущем фильтре.
//
// Часть 4 (отдельные маркеры компаний без выездов) НЕ рендерится — мы
// показываем только клиентов, у которых ЕСТЬ визиты в фильтре. Если визитов
// нет — на карте пусто. Это сознательно: без фильтра карта забивалась всеми
// компаниями портала.

import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { useAuth } from '../auth/AuthContext';
import type { EngineerListItem, VisitDto, VisitType } from '../api/types';
import { TYPE_COLOR, TYPE_LABEL } from '../styles/theme';
import { ErrorStrip } from './Dashboard';
import { VisitDetailDrawer } from './VisitDetail';

const BISHKEK: [number, number] = [42.8746, 74.5698];
const POLL_MS = 60_000;
const ALL_TYPES: VisitType[] = ['INSTALLATION', 'TRAINING', 'PAID_VISIT', 'ONLINE'];

type PeriodPreset = 'today' | 'tomorrow' | 'thisWeek' | 'nextWeek' | 'thisMonth' | 'custom';

const PRESET_LABEL: Record<PeriodPreset, string> = {
  today: 'Сегодня',
  tomorrow: 'Завтра',
  thisWeek: 'Эта неделя',
  nextWeek: 'След. неделя',
  thisMonth: 'Этот месяц',
  custom: 'Произвольный',
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function presetRange(p: PeriodPreset): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (p === 'today') return { from: ymd(today), to: ymd(today) };
  if (p === 'tomorrow') {
    const t = new Date(today);
    t.setDate(t.getDate() + 1);
    return { from: ymd(t), to: ymd(t) };
  }
  if (p === 'thisWeek' || p === 'nextWeek') {
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    if (p === 'nextWeek') monday.setDate(monday.getDate() + 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: ymd(monday), to: ymd(sunday) };
  }
  if (p === 'thisMonth') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { from: ymd(first), to: ymd(last) };
  }
  return { from: ymd(today), to: ymd(today) };
}

interface ClientGroup {
  /** Для маркеров клиентов — id клиента; для ad-hoc выездов — `adhoc-<visitId>`. */
  clientId: number | string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  visits: VisitDto[];
  dominantType: VisitType | 'mixed';
  isAdHoc: boolean;
}

function dominantType(visits: VisitDto[]): VisitType | 'mixed' {
  if (visits.length === 0) return 'mixed';
  const counts = new Map<VisitType, number>();
  for (const v of visits) counts.set(v.type, (counts.get(v.type) ?? 0) + 1);
  if (counts.size === 1) return Array.from(counts.keys())[0]!;
  return 'mixed';
}

function groupByClient(visits: VisitDto[]): ClientGroup[] {
  const map = new Map<string, ClientGroup>();
  for (const v of visits) {
    // Приоритет: customLocation > client. Кастомная локация — отдельный
    // маркер на каждый визит (не группируется), потому что это уникальная
    // точка для конкретного выезда (например, склад вместо офиса клиента).
    if (v.customLocation) {
      const key = `adhoc-${v.id}`;
      map.set(key, {
        clientId: key,
        name: v.client?.name ?? 'Адрес вручную',
        address: v.customLocation.address ?? v.client?.address ?? null,
        latitude: v.customLocation.latitude,
        longitude: v.customLocation.longitude,
        visits: [v],
        dominantType: v.type,
        isAdHoc: true,
      });
      continue;
    }
    if (!v.client) continue;
    if (v.client.latitude == null || v.client.longitude == null) continue;
    const key = String(v.client.id);
    const existing = map.get(key);
    if (existing) {
      existing.visits.push(v);
    } else {
      map.set(key, {
        clientId: v.client.id,
        name: v.client.name,
        address: v.client.address,
        latitude: v.client.latitude,
        longitude: v.client.longitude,
        visits: [v],
        dominantType: 'mixed', // переопределим ниже
        isAdHoc: false,
      });
    }
  }
  for (const g of map.values()) {
    if (!g.isAdHoc) g.dominantType = dominantType(g.visits);
  }
  return Array.from(map.values());
}

interface DrawerState {
  group: ClientGroup;
}

export function MapScreen() {
  const { api } = useAuth();

  // Фильтры
  const [preset, setPreset] = useState<PeriodPreset>('thisWeek');
  const initRange = useMemo(() => presetRange('thisWeek'), []);
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);
  const [engineerIds, setEngineerIds] = useState<Set<number>>(new Set());
  const [types, setTypes] = useState<VisitType[]>(ALL_TYPES);

  // Данные
  const [engineers, setEngineers] = useState<EngineerListItem[]>([]);
  const [visits, setVisits] = useState<VisitDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Sprint 7-task 3: toggle «Чекины» — оранжевые маркеры с координатами
  // arrivedLat/arrivedLng + пунктир до магазина. По умолчанию off — иначе
  // карту с большим периодом забивает шум прибытий.
  const [showCheckins, setShowCheckins] = useState(false);

  // Map refs
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  // Отдельный слой для чекинов — чтобы при toggle off можно было
  // одним вызовом clearLayers() убрать всё, не трогая основной слой клиентов.
  const checkinLayerRef = useRef<L.LayerGroup | null>(null);

  // Drawer (клик на маркер)
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [openVisitId, setOpenVisitId] = useState<number | null>(null);

  // --- Init Leaflet ---------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current).setView(BISHKEK, 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layersRef.current = L.layerGroup().addTo(map);
    checkinLayerRef.current = L.layerGroup().addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
      checkinLayerRef.current = null;
    };
  }, []);

  // --- Подмена preset → from/to --------------------------------------------
  useEffect(() => {
    if (preset === 'custom') return;
    const r = presetRange(preset);
    setFrom(r.from);
    setTo(r.to);
  }, [preset]);

  // --- Engineers list (один раз) -------------------------------------------
  // Спринт 6: на карте показываем только инженеров с isVisibleInSchedule=true.
  // Невидимые не появляются ни в фильтре, ни в визитах (выезды скрытых
  // инженеров просто не попадают на карту).
  useEffect(() => {
    api
      .listEngineers()
      .then((list) => setEngineers(list.filter((e) => e.isVisibleInSchedule)))
      .catch((e: Error) => setErr(e.message));
  }, [api]);

  // --- Reload visits с debounce 300мс + polling 60s ------------------------
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    async function load() {
      if (cancelled) return;
      setLoading(true);
      try {
        // Если выбран один инженер — фильтр по нему серверу. Если несколько
        // или все — тянем без engineerId и режем на клиенте (backend
        // принимает ровно один engineerId).
        const params: { from: string; to: string; limit: number; engineerId?: number } = {
          from,
          to,
          limit: 1000,
        };
        if (engineerIds.size === 1) {
          const [first] = engineerIds;
          if (first !== undefined) params.engineerId = first;
        }
        const resp = await api.listVisits(params);
        if (cancelled) return;
        setVisits(resp.items);
        setErr(null);
        setLastUpdate(new Date());
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    const debounce = setTimeout(load, 300);
    pollTimer = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [api, from, to, engineerIds]);

  // --- Фильтрация на клиенте (типы + multi-engineer + видимость) ----------
  const filteredVisits = useMemo(() => {
    let arr = visits;
    if (types.length < ALL_TYPES.length) arr = arr.filter((v) => types.includes(v.type));
    // Спринт 6: даже если backend вернул визит скрытого инженера (например,
    // в выборке без engineerId), на карту его не пускаем.
    const visibleIds = new Set(engineers.map((e) => e.id));
    arr = arr.filter((v) => visibleIds.has(v.engineer.id));
    if (engineerIds.size > 0 && engineerIds.size !== engineers.length) {
      arr = arr.filter((v) => engineerIds.has(v.engineer.id));
    }
    return arr;
  }, [visits, types, engineerIds, engineers]);

  const groups = useMemo(() => groupByClient(filteredVisits), [filteredVisits]);

  // Счётчики
  const counts = useMemo(() => {
    const clientIds = new Set<number>();
    const engIds = new Set<number>();
    for (const v of filteredVisits) {
      if (v.client) clientIds.add(v.client.id);
      engIds.add(v.engineer.id);
    }
    return { visits: filteredVisits.length, clients: clientIds.size, engineers: engIds.size };
  }, [filteredVisits]);

  // --- Render маркеров при изменении groups --------------------------------
  useEffect(() => {
    const layer = layersRef.current;
    if (!layer) return;
    layer.clearLayers();

    for (const g of groups) {
      const color = g.dominantType === 'mixed' ? '#64748b' : TYPE_COLOR[g.dominantType] ?? '#64748b';
      const count = g.visits.length;
      // Кастомный divIcon: круглый маркер цвета доминирующего типа + бейдж.
      const html = `
        <div style="position:relative;width:36px;height:36px">
          <div style="
            width:36px;height:36px;border-radius:50%;
            background:${color};border:3px solid #fff;
            box-shadow:0 2px 8px rgba(0,0,0,0.25);
            display:flex;align-items:center;justify-content:center;
            font-size:14px">🏢</div>
          ${count > 1 ? `<div style="
            position:absolute;top:-4px;right:-4px;
            min-width:18px;height:18px;padding:0 5px;
            background:#0f172a;color:#fff;border-radius:9px;
            font-size:11px;font-weight:700;
            display:flex;align-items:center;justify-content:center;
            border:2px solid #fff;box-sizing:content-box;
          ">${count}</div>` : ''}
        </div>`;
      const icon = L.divIcon({
        className: 'fd-client-marker',
        html,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
      const marker = L.marker([g.latitude, g.longitude], { icon });

      // Hover-tooltip: первые 3 визита + «+N ещё»
      const tooltipHtml = renderTooltipHtml(g);
      marker.bindTooltip(tooltipHtml, { direction: 'top', offset: [0, -16], opacity: 0.95 });

      // Клик — drawer
      marker.on('click', () => setDrawer({ group: g }));
      marker.addTo(layer);
    }
  }, [groups]);

  // --- Sprint 7-task 3: слой чекинов ---------------------------------------
  // Оранжевый circleMarker на arrivedLat/arrivedLng + пунктирная линия до
  // клиента/customLocation (если координаты есть). Popup: имя инженера,
  // время прибытия, расстояние в метрах.
  useEffect(() => {
    const layer = checkinLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showCheckins) return;

    for (const v of filteredVisits) {
      if (v.arrivedLat == null || v.arrivedLng == null) continue;
      // Координаты «куда инженер ехал» — приоритет customLocation > client.
      const targetLat = v.customLocation?.latitude ?? v.client?.latitude ?? null;
      const targetLng = v.customLocation?.longitude ?? v.client?.longitude ?? null;

      // Маркер чекина — оранжевый кружок.
      const checkinMarker = L.circleMarker([v.arrivedLat, v.arrivedLng], {
        radius: 7,
        fillColor: '#f97316',
        color: '#c2410c',
        weight: 2,
        opacity: 1,
        fillOpacity: 0.9,
      });

      const distM =
        targetLat != null && targetLng != null
          ? haversineMeters(v.arrivedLat, v.arrivedLng, targetLat, targetLng)
          : null;

      const arrivedHuman = v.arrivedAt ? formatIsoDateTime(v.arrivedAt) : '—';
      const popupHtml =
        `<div style="min-width:170px;font-size:12px">` +
        `<div style="font-weight:600;color:#0f172a">📍 ${escapeHtmlMap(v.engineer.fullName)}</div>` +
        `<div style="color:#64748b;margin-top:4px">Прибыл: ${arrivedHuman}</div>` +
        (distM != null
          ? `<div style="color:#475569;margin-top:4px">Расстояние до точки: <b>${Math.round(distM)} м</b></div>`
          : '<div style="color:#94a3b8;margin-top:4px">Расстояние: точка назначения без координат</div>') +
        `</div>`;
      checkinMarker.bindPopup(popupHtml);
      checkinMarker.bindTooltip(
        `${escapeHtmlMap(v.engineer.fullName)} · ${arrivedHuman}`,
        { direction: 'top', offset: [0, -10], opacity: 0.95 },
      );
      checkinMarker.addTo(layer);

      // Пунктирная линия чекин → точка назначения.
      if (targetLat != null && targetLng != null) {
        const line = L.polyline(
          [
            [v.arrivedLat, v.arrivedLng],
            [targetLat, targetLng],
          ],
          {
            color: '#f97316',
            weight: 2,
            opacity: 0.7,
            dashArray: '5, 10',
          },
        );
        line.addTo(layer);
      }
    }
  }, [filteredVisits, showCheckins]);

  function toggleEngineer(id: number) {
    setEngineerIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleType(t: VisitType) {
    setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  }

  return (
    <div style={{ height: '100%', display: 'flex' }}>
      {/* Map */}
      <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(15,23,42,0.85)',
            color: '#fff',
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            zIndex: 1000,
          }}
        >
          Найдено: {counts.visits} выездов · {counts.clients} клиентов · {counts.engineers} инженеров
          {lastUpdate && (
            <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>
              · обновлено {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
        {err && (
          <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 1000 }}>
            <ErrorStrip message={err} />
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div
        style={{
          width: 320,
          flexShrink: 0,
          background: '#fff',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <div style={{ padding: 16, borderBottom: '1px solid #e2e8f0' }}>
          <h1 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Карта выездов</h1>

          <FilterGroup label="Период">
            <select value={preset} onChange={(e) => setPreset(e.target.value as PeriodPreset)} style={selectStyle}>
              {(Object.keys(PRESET_LABEL) as PeriodPreset[]).map((p) => (
                <option key={p} value={p}>
                  {PRESET_LABEL[p]}
                </option>
              ))}
            </select>
            {preset === 'custom' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={selectStyle} />
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={selectStyle} />
              </div>
            )}
          </FilterGroup>

          <FilterGroup label="Инженеры">
            <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, padding: 6 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '3px 4px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={engineerIds.size === 0 || engineerIds.size === engineers.length}
                  onChange={() => setEngineerIds(new Set())}
                />
                Все инженеры
              </label>
              {engineers.map((e) => (
                <label
                  key={e.id}
                  style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '3px 4px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={engineerIds.has(e.id)}
                    onChange={() => toggleEngineer(e.id)}
                  />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.color, display: 'inline-block' }} />
                  {e.fullName}
                </label>
              ))}
            </div>
          </FilterGroup>

          <FilterGroup label="Типы">
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {ALL_TYPES.map((t) => {
                const active = types.includes(t);
                const c = TYPE_COLOR[t] ?? '#64748b';
                return (
                  <button
                    key={t}
                    onClick={() => toggleType(t)}
                    style={{
                      padding: '4px 8px',
                      fontSize: 11,
                      border: `1px solid ${active ? c : '#e2e8f0'}`,
                      background: active ? c + '18' : '#fff',
                      color: active ? c : '#64748b',
                      fontWeight: 600,
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                );
              })}
            </div>
          </FilterGroup>

          {/* Sprint 7-task 3: toggle оверлея чекинов */}
          <FilterGroup label="Дополнительно">
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                padding: '6px 4px',
                cursor: 'pointer',
                color: showCheckins ? '#c2410c' : '#475569',
              }}
            >
              <input
                type="checkbox"
                checked={showCheckins}
                onChange={(e) => setShowCheckins(e.target.checked)}
              />
              📍 Чекины инженеров
            </label>
            {showCheckins && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, lineHeight: 1.4 }}>
                Оранжевые точки — где инженер реально отметил прибытие.
                Пунктир — отклонение от точки назначения.
              </div>
            )}
          </FilterGroup>

        </div>

        {/* Список выездов */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 6 }}>
            Выезды в фильтре ({filteredVisits.length})
          </div>
          {loading && filteredVisits.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Загрузка…</div>
          ) : filteredVisits.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Нет визитов под выбранный фильтр.</div>
          ) : (
            filteredVisits
              .slice()
              .sort((a, b) => (a.scheduledDate + a.scheduledStart).localeCompare(b.scheduledDate + b.scheduledStart))
              .map((v) => <VisitListRow key={v.id} v={v} onOpen={() => setOpenVisitId(v.id)} />)
          )}
        </div>
      </div>

      {drawer && (
        <ClientDrawer
          group={drawer.group}
          onClose={() => setDrawer(null)}
          onOpenVisit={(id) => {
            setOpenVisitId(id);
            setDrawer(null);
          }}
        />
      )}

      {openVisitId != null && (
        <VisitDetailDrawer visitId={openVisitId} onClose={() => setOpenVisitId(null)} />
      )}
    </div>
  );
}

// --- Tooltip HTML (sanitized — все строки идут из нашего же DTO) -----------
function renderTooltipHtml(g: ClientGroup): string {
  const escape = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const previews = g.visits.slice(0, 3).map((v) => {
    const c = TYPE_COLOR[v.type] ?? '#64748b';
    return `<div style="margin-top:3px">
      <span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${c};margin-right:4px"></span>
      <b>${escape(TYPE_LABEL[v.type] ?? v.type)}</b> · ${escape(v.scheduledDate)} ${escape(v.scheduledStart)}
      <span style="color:#94a3b8"> · ${escape(v.engineer.fullName)}</span>
    </div>`;
  }).join('');
  const more = g.visits.length > 3 ? `<div style="margin-top:4px;color:#64748b">+${g.visits.length - 3} ещё</div>` : '';
  return `
    <div style="font-size:12px;line-height:1.4">
      <div style="font-weight:700;color:#0f172a">${escape(g.name)}</div>
      ${g.address ? `<div style="color:#64748b;margin-top:2px">${escape(g.address)}</div>` : ''}
      <div style="color:#475569;margin-top:6px"><b>${g.visits.length}</b> ${pluralize(g.visits.length)}</div>
      ${previews}${more}
    </div>`;
}

function pluralize(n: number): string {
  const last2 = n % 100;
  if (last2 >= 11 && last2 <= 14) return 'выездов';
  const last = n % 10;
  if (last === 1) return 'выезд';
  if (last >= 2 && last <= 4) return 'выезда';
  return 'выездов';
}

// --- Helpers для слоя чекинов (Sprint 7-task 3) ----------------------------
function escapeHtmlMap(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function formatIsoDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Расстояние между двумя точками в метрах (формула Haversine, R = 6371 км). */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// --- Small components -------------------------------------------------------
function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 5, letterSpacing: 0.4 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 12,
  background: '#fff',
};

function VisitListRow({ v, onOpen }: { v: VisitDto; onOpen(): void }) {
  const c = TYPE_COLOR[v.type] ?? '#64748b';
  return (
    <button
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: '#f8fafc',
        border: 'none',
        borderLeft: `3px solid ${c}`,
        borderRadius: 6,
        padding: '7px 10px',
        marginBottom: 6,
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 11, color: '#64748b' }}>
        {v.scheduledDate} · {v.scheduledStart}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', marginTop: 2 }}>
        {v.client?.name ?? (v.bitrixDealId ? `Сделка #${v.bitrixDealId}` : (v.customLocation?.address ?? '—'))}
      </div>
      <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>
        <span style={{ color: c, fontWeight: 600 }}>{TYPE_LABEL[v.type]}</span> · {v.engineer.fullName}
      </div>
    </button>
  );
}

function ClientDrawer({
  group,
  onClose,
  onOpenVisit,
}: {
  group: ClientGroup;
  onClose(): void;
  onOpenVisit(id: number): void;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420,
          maxWidth: '92vw',
          height: '100%',
          background: '#fff',
          overflowY: 'auto',
          boxShadow: '-6px 0 24px rgba(15,23,42,0.18)',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Клиент</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{group.name}</div>
            {group.address && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>📍 {group.address}</div>
            )}
            <div style={{ fontSize: 11, color: '#475569', marginTop: 8 }}>
              <b>{group.visits.length}</b> {pluralize(group.visits.length)} в фильтре
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8' }}>
            ✕
          </button>
        </div>
        <div style={{ padding: 16 }}>
          {group.visits
            .slice()
            .sort((a, b) => (a.scheduledDate + a.scheduledStart).localeCompare(b.scheduledDate + b.scheduledStart))
            .map((v) => (
              <VisitListRow key={v.id} v={v} onOpen={() => onOpenVisit(v.id)} />
            ))}
        </div>
      </div>
    </div>
  );
}

